/**
 * Capabilities store — fetches user capabilities from GET /auth/capabilities.
 *
 * The backend resolves capabilities by merging role-based permissions
 * with org-level feature flags (ADM-007). If the API is unavailable,
 * falls back to viewer-level (no permissions).
 */
import { create } from 'zustand'
import { authAPI, type CapabilitiesResponse } from '@/api/auth'

export interface Capabilities {
  /** Can access /admin/* routes */
  adminAccess: boolean
  /** Can read organizations */
  orgsRead: boolean
  /** Can create and modify organizations */
  orgsManage: boolean
  /** Can manage users (create, edit, deactivate) */
  usersManage: boolean
  /** Can manage access groups */
  groupsManage: boolean
  /** Can manage RLS bindings */
  rlsManage: boolean
  /** Can manage invites */
  invitesManage: boolean
  /** Can read platform RBAC roles, assignments and elevations */
  platformRbacRead: boolean
  /** Can create/revoke platform RBAC assignments and elevations */
  platformRbacManage: boolean
  /** Can access still-legacy superadmin-only features */
  superadminAccess: boolean
  /** Can impersonate organizations */
  impersonate: boolean
}

interface CapabilitiesState {
  capabilities: Capabilities
  isLoading: boolean
  isResolved: boolean
  resolvedForKey: string | null
  /** Fetch capabilities from backend API. Falls back to viewer-level on error. */
  fetchCapabilities: (fetchKey: string, force?: boolean) => Promise<void>
  /** Reset to viewer-level (used on logout). */
  resetCapabilities: () => void
}

export const EMPTY_CAPABILITIES: Capabilities = {
  adminAccess: false,
  orgsRead: false,
  orgsManage: false,
  usersManage: false,
  groupsManage: false,
  rlsManage: false,
  invitesManage: false,
  platformRbacRead: false,
  platformRbacManage: false,
  superadminAccess: false,
  impersonate: false,
}

let inFlightFetch: Promise<void> | null = null

function mapCapabilities(res: CapabilitiesResponse): Capabilities {
  return {
    adminAccess: res.admin_access,
    orgsRead: res.orgs_read,
    orgsManage: res.orgs_manage,
    usersManage: res.users_manage,
    groupsManage: res.groups_manage,
    rlsManage: res.rls_manage,
    invitesManage: res.invites_manage,
    platformRbacRead: res.platform_rbac_read,
    platformRbacManage: res.platform_rbac_manage,
    superadminAccess: res.superadmin_access,
    impersonate: res.impersonate,
  }
}

export const useCapabilitiesStore = create<CapabilitiesState>()((set) => ({
  capabilities: EMPTY_CAPABILITIES,
  isLoading: false,
  isResolved: false,
  resolvedForKey: null,

  fetchCapabilities: async (fetchKey, force = false) => {
    const state = useCapabilitiesStore.getState()
    const sameKey = state.resolvedForKey === fetchKey
    const keepResolved = force && sameKey && state.isResolved

    if (!force && state.isResolved && state.resolvedForKey === fetchKey) {
      return
    }
    if (state.isLoading && sameKey && inFlightFetch) {
      return inFlightFetch
    }

    set({
      isLoading: true,
      isResolved: keepResolved ? true : false,
      resolvedForKey: fetchKey,
    })
    inFlightFetch = (async () => {
      try {
        const response = await authAPI.getCapabilities()
        set({
          capabilities: mapCapabilities(response),
          isLoading: false,
          isResolved: true,
          resolvedForKey: fetchKey,
        })
      } catch {
        // Fallback: viewer-level (no permissions) if API unavailable
        set({
          capabilities: EMPTY_CAPABILITIES,
          isLoading: false,
          isResolved: true,
          resolvedForKey: fetchKey,
        })
      } finally {
        inFlightFetch = null
      }
    })()

    return inFlightFetch
  },

  resetCapabilities: () => {
    inFlightFetch = null
    set({ capabilities: EMPTY_CAPABILITIES, isLoading: false, isResolved: false, resolvedForKey: null })
  },
}))
